import { api, ApiError, downloadFile } from "../api.js";
import { esc, difficultyBadgeClass, siteLocationLabel, haversineKm, formatDistanceKm, toast } from "../utils.js";
import { getCurrentUser } from "../auth.js";
import { signInPromptHtml, attachSignInLinks } from "../signInPrompt.js";
import { navigate } from "../app.js";
import { infiniteScroll } from "../infiniteScroll.js";
import { tabSelectorHtml, attachTabSelector } from "../tabbedSection.js";
import { renderSiteMap } from "../siteMap.js";
import { getPage } from "../api.js";
import { lastKnownLocation, rememberLocation } from "../lastLocation.js";

// Where the reader is, and how sure we are.
//
// These used to be module-level, so a second visit in the same session
// did not re-prompt. lastLocation.js does that job properly now — across
// sessions, not just this one — so they are per-render again. Module
// state that outlives a render is also state that leaks between tests,
// and did.
//
// geoStatus: "locating" | "found" | "remembered" | "unavailable".
// "remembered" is the honest middle: ordering by distance, but from
// where they were last time rather than where they are now.

function siteCardHtml(site) {
  const difficulty = site.difficulty
    ? `<span class="${difficultyBadgeClass(site.difficulty)}">${esc(site.difficulty)}</span>`
    : "";
  const depth = site.max_depth_m ? `<span class="ms-3"><i class="bi bi-water me-1"></i>${site.max_depth_m}m</span>` : "";
  const location = siteLocationLabel(site);
  const distanceBadge = site._distanceKm != null
    ? `<span class="badge text-bg-light border"><i class="bi bi-signpost-2 me-1"></i>${formatDistanceKm(site._distanceKm)}</span>`
    : "";
  return `
    <div class="card mb-3 card-dc-clickable" data-site-id="${site.id}">
      <div class="card-body">
        <div class="d-flex justify-content-between align-items-start mb-2">
          <h3 class="h6 fw-semibold mb-0">${esc(site.name)}</h3>
          <div class="d-flex align-items-center gap-2">${distanceBadge}${difficulty}</div>
        </div>
        ${site.description ? `<p class="text-slate small mb-2 dc-user-text">${esc(site.description)}</p>` : ""}
        ${location ? `<div class="text-slate small mb-1"><i class="bi bi-geo-alt me-1"></i>${esc(location)}</div>` : ""}
        <div class="text-slate small">
          <i class="bi bi-compass me-1"></i>${site.latitude.toFixed(3)}, ${site.longitude.toFixed(3)}${depth}
        </div>
      </div>
    </div>`;
}

/** Keeps the echoed search term short enough that the notice stays on
 *  one line.
 *
 * The line above the map reserves exactly one line of space so the map
 * does not move as notices come and go. A long search term would wrap it
 * to two and defeat that — measured at 360px, which is where it bites. */
function shortenTerm(term, limit = 16) {
  return term.length <= limit ? term : `${term.slice(0, limit - 1)}…`;
}

function emptyStateHtml() {
  return `<div class="dc-empty"><i class="bi bi-compass"></i><div class="fw-semibold">No dive sites found</div><div>Add the first one for your cohort to explore.</div></div>`;
}

/** Attaches the distance badge. The *order* now comes from the server —
 * it is given the same coordinates and sorts nearest-first — so this no
 * longer re-sorts. Sorting here would only have reordered the pages
 * already on screen, which is exactly the bug that made distance and
 * paging mutually exclusive before. */
function withDistances(sites, userCoords) {
  if (!userCoords) return sites;
  return sites.map((s) => ({
    ...s,
    _distanceKm: haversineKm(userCoords.lat, userCoords.lng, s.latitude, s.longitude),
  }));
}

// Module-level so the next render can stop the last visit's list and
// its pending search: either would otherwise run against this visit.
// The search term itself is per-render (see render()).
let debounceHandle;
let activeSites = null;

function bindCards(root) {
  root.querySelectorAll("[data-site-id]").forEach((card) => {
    card.addEventListener("click", () => navigate(`/dive-site/${card.dataset.siteId}`));
  });
}

/**
 * One mode now: the server orders, so paging works either way.
 *
 * When the reader's location is known it is sent along and the API
 * returns sites nearest-first; without it the order is alphabetical.
 * Either way the list pages in as they scroll, and the distance badge
 * is computed per card from the coordinates already in the payload.
 */
function loadSites(container, query, userCoords) {
  activeSites?.destroy();

  const params = {};
  if (query) params.q = query;
  if (userCoords) {
    params.lat = userCoords.lat;
    params.lng = userCoords.lng;
  }

  activeSites = infiniteScroll(container, {
    path: "/dive-sites",
    query: params,
    emptyHtml: emptyStateHtml(),
    onError: (err) => {
      container.innerHTML = `<div class="dc-empty"><i class="bi bi-exclamation-triangle"></i><div>${esc(err.message || "Could not load dive sites.")}</div></div>`;
    },
    render: (list, sites) => {
      const batch = document.createElement("div");
      batch.innerHTML = withDistances(sites, userCoords).map(siteCardHtml).join("");
      bindCards(batch);
      list.append(batch);
    },
  });
}

/** What the map asks for.
 *
 * Sites in the current viewport rather than the whole directory: a pin
 * you cannot see is the same as a missing one, but a pin outside the
 * view is not worth fetching. The endpoint takes ?bbox=, so panning
 * asks for what is now on screen instead of paging through everything.
 *
 * The page cap remains as a guard for a single very dense view — zoomed
 * out over the whole world, every site is technically in the box.
 */
const MAP_PAGE_SIZE = 100;
const MAP_MAX_PAGES = 5;

async function loadSitesInView(query) {
  const sites = [];
  let cursor = null;
  for (let page = 0; page < MAP_MAX_PAGES; page += 1) {
    const { items, nextCursor } = await getPage("/dive-sites", {
      query,
      cursor,
      limit: MAP_PAGE_SIZE,
    });
    sites.push(...items);
    cursor = nextCursor;
    if (!cursor) return { sites, complete: true };
  }
  // A view holding more sites than we will fetch. The map still works;
  // it is just not all of them, and saying so beats pretending. Zooming
  // in narrows the box and clears the notice on its own.
  return { sites, complete: false };
}

export function render(container) {
  // Visitors browse and export; adding and importing need an account.
  const signedIn = getCurrentUser() != null;
  // Per visit, like the search box it mirrors, which starts empty. A
  // module-level term outlived the page: back on it, the list was still
  // filtered by a search the box no longer showed.
  let currentQuery = "";
  clearTimeout(debounceHandle);
  let userCoords = null;
  let geoStatus = "locating";
  // Whether a fix is in flight. Kept apart from geoStatus so asking for
  // one does not overwrite the fact that we are already ordering by a
  // remembered place.
  let locating = false;

  // A remembered place, so a refused prompt still gets nearest-first
  // rather than alphabetical.
  const remembered = lastKnownLocation();
  if (remembered) {
    userCoords = { lat: remembered.lat, lng: remembered.lng };
    geoStatus = "remembered";
  }

  container.innerHTML = `
    <div class="d-flex justify-content-between align-items-center mb-3">
      <h1 class="display-heading h3 mb-0">Dive Sites</h1>
      <div class="d-flex gap-2">
        <div class="dropdown">
          <button class="btn btn-outline-secondary" type="button" id="export-sites-btn"
                  data-bs-toggle="dropdown" aria-expanded="false" title="Download dive sites for Subsurface">
            <i class="bi bi-download me-1"></i>Export
          </button>
          <div class="dropdown-menu dropdown-menu-end">
            <button class="dropdown-item" type="button" id="export-shown-btn">Sites shown here</button>
            ${getCurrentUser() ? `<button class="dropdown-item" type="button" id="export-mine-btn">Sites I added</button>` : ""}
          </div>
        </div>
        ${signedIn ? `
        <button class="btn btn-outline-secondary" id="import-sites-btn" title="Import dive sites from a Subsurface dive log">
          <i class="bi bi-upload me-1"></i>Import
        </button>
        <button class="btn btn-accent" id="new-site-btn"><i class="bi bi-plus-lg me-1"></i>Add Site</button>` : ""}
      </div>
    </div>
    ${signedIn ? "" : signInPromptHtml(
      "Log in or sign up to add dive sites, post about them and plan dives with other divers.")}
    <div class="mb-3">
      <input type="search" class="form-control" id="site-search" placeholder="Search dive sites..." />
    </div>
    <div class="mb-2 text-slate small" id="location-status"></div>
    <div class="mb-3" id="sites-tabs-host"></div>
    <div id="sites-content"></div>
    <div id="sites-map-panel" class="d-none"></div>`;

  const content = container.querySelector("#sites-content");
  const mapPanel = container.querySelector("#sites-map-panel");
  const locationStatus = container.querySelector("#location-status");

  function renderLocationStatus() {
    if (geoStatus === "found") {
      locationStatus.innerHTML = `<i class="bi bi-geo-alt-fill me-1"></i>Sorted by distance from your location`;
    } else if (geoStatus === "remembered") {
      // Named as remembered rather than passed off as current: sorting
      // by somewhere they were last month, silently, is worse than
      // alphabetical, because it looks right.
      //
      // The trailing clause matters while a fix is still pending. The
      // list is *already* ordered by the remembered place, so saying
      // only "finding your location" would describe the request rather
      // than the thing on screen.
      const tail = locating
        ? `<span class="ms-1">checking your location…</span>`
        : `<button class="btn btn-link btn-sm p-0 align-baseline" id="retry-location-btn">use my location</button>`;
      locationStatus.innerHTML = `<i class="bi bi-geo-alt me-1"></i>Sorted by distance from where you last looked — ${tail}`;
      const retryBtn = locationStatus.querySelector("#retry-location-btn");
      if (retryBtn) retryBtn.addEventListener("click", requestLocation);
    } else if (geoStatus === "unavailable") {
      locationStatus.innerHTML = `<i class="bi bi-geo-alt me-1"></i>Location unavailable — <button class="btn btn-link btn-sm p-0 align-baseline" id="retry-location-btn">try again</button> to sort by distance`;
      const retryBtn = locationStatus.querySelector("#retry-location-btn");
      if (retryBtn) retryBtn.addEventListener("click", requestLocation);
    } else {
      locationStatus.innerHTML = `<i class="bi bi-geo-alt me-1"></i>Finding your location to sort by distance...`;
    }
  }

  /** Falls back to whatever we remembered. Used by both the "no
   *  geolocation here at all" path and the "you said no" path — the
   *  first of those used to throw the memory away and drop back to
   *  alphabetical, which is the case on any page not served over
   *  HTTPS. */
  function fallBackToMemory() {
    locating = false;
    const stored = lastKnownLocation();
    if (!stored) return false;

    // render() already seeds from the same memory, so on a page where
    // geolocation is simply unavailable this would otherwise refetch
    // the list with coordinates it was already sorted by — one wasted
    // request per visit. Only reload when something actually changed.
    const unchanged =
      userCoords && userCoords.lat === stored.lat && userCoords.lng === stored.lng;

    userCoords = { lat: stored.lat, lng: stored.lng };
    geoStatus = "remembered";
    renderLocationStatus();
    if (!unchanged) loadSites(content, currentQuery, userCoords);
    return true;
  }

  function requestLocation() {
    if (!("geolocation" in navigator)) {
      if (fallBackToMemory()) return;
      geoStatus = "unavailable";
      renderLocationStatus();
      return;
    }
    locating = true;
    if (geoStatus !== "remembered") geoStatus = "locating";
    renderLocationStatus();
    navigator.geolocation.getCurrentPosition(
      (position) => {
        locating = false;
        userCoords = { lat: position.coords.latitude, lng: position.coords.longitude };
        geoStatus = "found";
        // Kept for next time, and it outranks any remembered map centre.
        rememberLocation({ ...userCoords, source: "device" });
        renderLocationStatus();
        // Refetch rather than re-sort: the server owns the order now, so
        // the coordinates have to reach it. Sorting the pages already on
        // screen would only order what happened to be loaded.
        loadSites(content, currentQuery, userCoords);
        // The map is not ordered, so it needs no refetch — but if it is
        // open it should move to where we now know the reader is.
        if (activeTab === "map" && activeMap?.showNearby && userCoords) {
          activeMap.showNearby(userCoords.lat, userCoords.lng);
        }
      },
      () => {
        locating = false;
        // Refused or timed out. If we know where they were, use that
        // instead of giving up on distance ordering altogether.
        if (fallBackToMemory()) return;
        geoStatus = "unavailable";
        renderLocationStatus();
      },
      { enableHighAccuracy: false, timeout: 8000, maximumAge: 5 * 60 * 1000 }
    );
  }

  // ---- List / Map ----
  let activeTab = "list";
  let activeMap = null;
  // Rises with every map load so a slow earlier fetch cannot draw over
  // a newer one — the same guard the search picker needs.
  let mapGeneration = 0;

  const tabsHost = container.querySelector("#sites-tabs-host");

  function renderTabs() {
    tabsHost.innerHTML = tabSelectorHtml(
      [
        { id: "list", label: "List" },
        { id: "map", label: "Map" },
      ],
      activeTab,
      { id: "sites-tabs", label: "How to show dive sites" }
    );
    attachTabSelector(tabsHost, (id) => {
      if (id === activeTab) return;
      activeTab = id;
      renderTabs();
      showTab();
    });
  }

  function showTab() {
    const onMap = activeTab === "map";
    content.classList.toggle("d-none", onMap);
    mapPanel.classList.toggle("d-none", !onMap);
    if (onMap) loadMap();
  }

  function mapQuery(bbox) {
    const query = {};
    if (currentQuery) query.q = currentQuery;
    if (bbox) query.bbox = bbox;
    return query;
  }

  function setPartialNotice(complete, shown) {
    const notice = mapPanel.querySelector("#map-partial");
    if (!notice) return;

    /** Sets the line. The element's height is reserved in CSS, so this
     *  only changes the words — never the position of the map. */
    const show = (html) => {
      notice.innerHTML = html;
    };

    if (!complete) {
      show(`<i class="bi bi-info-circle me-1"></i>${shown} shown — zoom in for the rest.`);
      return;
    }
    // A search that matches nothing here is worth saying out loud. The
    // view deliberately stays where it is while you type, so without
    // this an empty map looks like the search is broken rather than
    // like the matches being somewhere else.
    show(
      shown === 0 && currentQuery
        ? `<i class="bi bi-info-circle me-1"></i>No matches for “${esc(shortenTerm(currentQuery))}” here — pan or zoom out.`
        : ""
    );
  }

  /** Fetches the sites in `bbox` and swaps the pins, leaving the view
   *  where it is.
   *
   *  `firstLoad` changes only what a failure looks like. A pan that
   *  fails should leave the pins that are on screen and try again on
   *  the next pan — blanking the map because one request failed mid-drag
   *  would be worse than a moment of stale pins. A first load that fails
   *  has no pins to keep, so silence there is just an empty map with no
   *  explanation. */
  async function loadMapView(bbox, { firstLoad = false } = {}) {
    const mine = ++mapGeneration;
    let result;
    try {
      result = await loadSitesInView(mapQuery(bbox));
    } catch (err) {
      if (mine !== mapGeneration) return;
      if (firstLoad) {
        activeMap?.destroy();
        activeMap = null;
        mapPanel.innerHTML = `<div class="dc-empty"><i class="bi bi-exclamation-triangle"></i><div>${esc(err.message || "Could not load dive sites.")}</div></div>`;
      }
      return;
    }
    if (mine !== mapGeneration || !activeMap) return;
    activeMap.setSites(result.sites);
    setPartialNotice(result.complete, result.sites.length);
  }

  /** First build of the map.
   *
   * The map is created before anything is fetched, because it always
   * knows where to open — a view the reader left, twenty miles of a
   * known position, or North America. Its own bounds are then the box
   * worth asking for, so the first request is already narrowed and
   * there is no unbounded "fetch the world" step.
   */
  async function loadMap() {
    activeMap?.destroy();
    activeMap = null;

    // Above the map, not below it. Both notices are about what you are
    // looking at and what to do about it — pan, or zoom out — and under
    // a 70vh map they sat below the fold on most screens, so the reader
    // never saw the one thing that explained an empty map.
    //
    // The line keeps its space whether or not it has anything in it
    // (see .dc-map-notice): otherwise the map jumps down as a notice
    // appears and back up as it goes, which it does while you are
    // typing in the search box.
    mapPanel.innerHTML = `
      <div class="text-slate small dc-map-notice mb-2" id="map-partial"></div>
      <div id="sites-map" class="dc-map"></div>`;
    activeMap = renderSiteMap(mapPanel.querySelector("#sites-map"), [], {
      onViewChange: loadMapView,
      restoreView: lastKnownLocation(),
    });
    // The panel was display:none until a moment ago, so Leaflet measured
    // it as zero and drew a single tile. Tell it to look again before
    // reading the bounds off it.
    activeMap.refresh?.();
    await loadMapView(activeMap.currentBBox(), { firstLoad: true });
  }

  renderLocationStatus();
  renderTabs();
  loadSites(content, currentQuery, userCoords);
  requestLocation();

  container.querySelector("#site-search").addEventListener("input", (e) => {
    clearTimeout(debounceHandle);
    currentQuery = e.target.value.trim();
    debounceHandle = setTimeout(() => {
      // Both views answer the same search; only the visible one refetches.
      if (activeTab !== "map") {
        loadSites(content, currentQuery, userCoords);
        return;
      }
      // Refetch for the view the reader is looking at, rather than
      // rebuilding the map. loadMap() re-chooses the opening view, so
      // every keystroke used to throw away wherever they had panned to
      // — typing a letter sent them back to North America.
      if (activeMap) loadMapView(activeMap.currentBBox());
      else loadMap();
    }, 350);
  });

  container.querySelector("#new-site-btn")?.addEventListener("click", () => navigate("/create-dive-site"));
  container.querySelector("#import-sites-btn")?.addEventListener("click", () => navigate("/import-dive-sites"));
  attachSignInLinks(container);

  async function exportSites(query) {
    try {
      await downloadFile("/dive-sites/export", query);
    } catch (err) {
      toast(err instanceof ApiError ? err.message : "Could not export the dive sites.", "danger");
    }
  }

  // What's on screen: the search's matches in the list, or on the map
  // the matches in view.
  container.querySelector("#export-shown-btn").addEventListener("click", () => {
    const onMap = activeTab === "map" && activeMap;
    exportSites(onMap ? mapQuery(activeMap.currentBBox()) : mapQuery(null));
  });
  container.querySelector("#export-mine-btn")?.addEventListener("click", () => {
    exportSites({ created_by: getCurrentUser().id });
  });
}
